import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Tag,
  Button,
  Space,
  Divider,
  Avatar,
  List,
  Input,
  Form,
  message,
  Spin,
  Row,
  Col
} from 'antd';
import {
  EyeOutlined,
  LikeOutlined,
  MessageOutlined,
  CalendarOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  LeftOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import dayjs from 'dayjs';
import { api } from '../../api/mockData';
import './index.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    fetchPostDetails();
  }, [id]);

  const fetchPostDetails = async () => {
    setLoading(true);
    try {
      const postData = await api.getPost(parseInt(id));
      setPost(postData);
      setLikeCount(postData.likeCount);
      // 模拟获取评论
      setComments([
        {
          id: 1,
          author: '李四',
          avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop',
          content: '这篇文章写得真好，学到了很多！',
          datetime: '2023-10-18 14:30:00'
        },
        {
          id: 2,
          author: '王五',
          avatar: 'https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=100&h=100&fit=crop',
          content: '感谢分享，期待更多关于React Hooks的内容！',
          datetime: '2023-10-17 09:15:00'
        }
      ]);
    } catch (error) {
      message.error('文章不存在或已被删除');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = () => {
    if (liked) {
      setLikeCount(prev => prev - 1);
    } else {
      setLikeCount(prev => prev + 1);
    }
    setLiked(!liked);
    message.success(liked ? '取消点赞' : '点赞成功');
  };

  const handleCommentSubmit = async (values) => {
    setCommentLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newComment = {
        id: comments.length + 1,
        author: '当前用户',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
        content: values.comment,
        datetime: dayjs().format('YYYY-MM-DD HH:mm:ss')
      };
      
      setComments([newComment, ...comments]);
      form.resetFields();
      message.success('评论发表成功');
    } catch (error) {
      message.error('评论失败，请重试');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    message.success('链接已复制到剪贴板');
  };

  const handleEdit = () => {
    navigate(`/edit/${id}`);
  };

  const handleDelete = () => {
    message.warning('删除功能开发中');
  };

  if (loading || !post) {
    return (
      <div className="spin-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="post-detail-page">
      {/* 返回按钮 */}
      <Button
        type="text"
        icon={<LeftOutlined />}
        onClick={() => navigate(-1)}
        className="back-btn"
      >
        返回列表
      </Button>

      {/* 文章头部 */}
      <Card className="post-header-card">
        <Title level={1} className="post-title">{post.title}</Title>
        
        <div className="post-meta">
          <Space size="large" wrap>
            <Space size="small">
              <Avatar size="small" icon={<UserOutlined />} />
              <Text strong>{post.author}</Text>
            </Space>
            
            <Space size="small">
              <CalendarOutlined />
              <Text type="secondary">
                {dayjs(post.createdAt).format('YYYY年MM月DD日')}
              </Text>
            </Space>
            
            <Space size="small">
              <EyeOutlined />
              <Text type="secondary">{post.viewCount} 次阅读</Text>
            </Space>
          </Space>
        </div>

        <div className="post-tags">
          {post.tags.map(tag => (
            <Tag key={tag} color="processing" className="post-tag">
              {tag}
            </Tag>
          ))}
        </div>
      </Card>

      {/* 文章内容 */}
      <Card className="post-content-card">
        <div className="markdown-content">
          <ReactMarkdown
            children={post.content}
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
            components={{
              code({node, inline, className, children, ...props}) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    children={String(children).replace(/\n$/, '')}
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  />
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
            }}
          />
        </div>
      </Card>

      {/* 文章操作栏 */}
      <Card className="post-actions-card">
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="middle">
              <Button
                type={liked ? 'primary' : 'default'}
                icon={<LikeOutlined />}
                onClick={handleLike}
              >
                赞 ({likeCount})
              </Button>
              <Button
                type="text"
                icon={<MessageOutlined />}
                onClick={() => document.getElementById('comment-input')?.focus()}
              >
                评论 ({comments.length})
              </Button>
              <Button
                type="text"
                icon={<ShareAltOutlined />}
                onClick={handleShare}
              >
                分享
              </Button>
            </Space>
          </Col>
          <Col>
            <Space size="small">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={handleEdit}
              >
                编辑
              </Button>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              >
                删除
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 评论区域 */}
      <Card className="comments-card" title={`评论 (${comments.length})`}>
        {/* 发表评论 */}
        <div className="comment-form">
          <Form form={form} onFinish={handleCommentSubmit}>
            <Form.Item
              name="comment"
              rules={[
                { required: true, message: '请输入评论内容' },
                { max: 500, message: '评论不能超过500字' }
              ]}
            >
              <TextArea
                id="comment-input"
                placeholder="写下你的评论..."
                rows={4}
                showCount
                maxLength={500}
                className="comment-input"
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={commentLoading}
                className="comment-submit"
              >
                发表评论
              </Button>
            </Form.Item>
          </Form>
        </div>

        <Divider />

        {/* 评论列表 - 使用List组件代替Comment */}
        <List
          dataSource={comments}
          renderItem={comment => (
            <List.Item className="comment-item">
              <List.Item.Meta
                avatar={<Avatar src={comment.avatar} />}
                title={<Text strong>{comment.author}</Text>}
                description={
                  <>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {comment.datetime}
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      {comment.content}
                    </div>
                  </>
                }
              />
            </List.Item>
          )}
          locale={{ emptyText: '暂无评论，快来抢沙发吧～' }}
        />
      </Card>
    </div>
  );
};

export default PostDetail;